import React, { useEffect, useState, useCallback, useRef } from 'react';
import Container from '../Container/Container';
import { useDispatch, useSelector } from 'react-redux';
import { get, post } from '../ApiServices/ApiServices';
import { useNavigate } from 'react-router';
import Loader from './Component/Loader';
import { toast } from 'react-toastify';
import moment from 'moment';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Returns the most-recent Saturday PRIOR to today as a moment object */
function getPriorSaturday() {
	const today = moment();
	const day = today.day(); // 0=Sun … 6=Sat
	const daysBack = day === 6 ? 7 : day + 1; // if today IS Sat, go back 7
	return today.clone().subtract(daysBack, 'days');
}

// ─── Main Component ──────────────────────────────────────────────────────────

function ProcessReturns() {
	const navigate = useNavigate();
	const profile = useSelector((state) => state.profile.profile);

	// Modal / processing state
	const [weekEndingDate, setWeekEndingDate] = useState(
		getPriorSaturday().format('MM/DD/YYYY')
	);
	const [dateError, setDateError] = useState('');
	const [isProcessing, setIsProcessing] = useState(false);

	// History list
	const [history, setHistory] = useState([
		{
			processedAt: moment('2026-03-27 18:00', 'YYYY-MM-DD HH:mm'),
			weekEndingDate: moment('2026-03-21'),
			total: 600,
			returned: 595,
			notReturned: 5,
		},
		{
			processedAt: moment('2026-03-20 17:00', 'YYYY-MM-DD HH:mm'),
			weekEndingDate: moment('2026-03-14'),
			total: 500,
			returned: 490,
			notReturned: 10,
		},
	]);

	// Pagination for history
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);

	// Refs
	const confirmModalCloseRef = useRef(null);

	// ─── Handlers ───────────────────────────────────────────────────────────

	const handleNo = () => {
		// Close modal, reset date to default, no action
		setWeekEndingDate(getPriorSaturday().format('MM/DD/YYYY'));
		setDateError('');
		if (confirmModalCloseRef.current) confirmModalCloseRef.current.click();
	};

	const handleYes = async () => {
		// Validate date
		const parsed = moment(weekEndingDate, 'MM/DD/YYYY', true);
		if (!parsed.isValid()) {
			setDateError('Please enter a valid date (MM/DD/YYYY).');
			return;
		}
		if (parsed.isAfter(moment(), 'day')) {
			setDateError('Week-ending date cannot be in the future.');
			return;
		}

		setDateError('');
		setIsProcessing(true);

		// Close modal immediately
		if (confirmModalCloseRef.current) confirmModalCloseRef.current.click();

		try {
			const lookbackDate = moment().subtract(10, 'months').format('YYYY-MM-DD');
			const endDate = parsed.format('YYYY-MM-DD');

			const response = await post('process-returns', {
				start_date: lookbackDate,
				end_date: endDate,
			});

			if (response?.status === true) {
				const result = {
					processedAt: moment(),
					weekEndingDate: parsed.clone(),
					total: response?.data?.total || 0,
					returned: response?.data?.returned || 0,
					notReturned: response?.data?.notReturned || 0,
				};
				setHistory(prev => [result, ...prev]);
				setCurrentPage(1);

				toast.success(response?.message || 'Returns processed successfully!', {
					position: 'top-right',
					autoClose: 2000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					closeButton: false,
				});
			} else {
				toast.error(response?.message || 'Processing failed. Please try again.', {
					position: 'top-right',
					autoClose: 2000,
					hideProgressBar: false,
					closeOnClick: false,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					closeButton: false,
				});
			}
		} catch (err) {
			if (
				err?.message === 'Invalid or expired token' ||
				err?.message === 'Invalid Token'
			) {
				localStorage.removeItem('token');
				navigate('/login');
				return;
			}
			toast.error(err?.message || 'Something went wrong.', {
				position: 'top-right',
				autoClose: 2000,
				hideProgressBar: false,
				closeOnClick: false,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				closeButton: false,
			});
		} finally {
			setIsProcessing(false);
		}
	};

	// ─── Pagination ──────────────────────────────────────────────────────────

	const totalPages = Math.ceil(history.length / itemsPerPage);
	const paginatedHistory = history.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage
	);

	const handlePageChange = (page) => {
		if (page >= 1 && page <= totalPages) setCurrentPage(page);
	};

	const handleItemsPerPageChange = (e) => {
		setItemsPerPage(Number(e.target.value));
		setCurrentPage(1);
	};

	const renderPaginationButtons = () => {
		const buttons = [];
		const maxVisible = 5;
		let startPage, endPage;

		if (totalPages <= maxVisible) {
			startPage = 1;
			endPage = totalPages;
		} else {
			const before = Math.floor(maxVisible / 2);
			const after = Math.ceil(maxVisible / 2) - 1;
			if (currentPage <= before) {
				startPage = 1; endPage = maxVisible;
			} else if (currentPage + after >= totalPages) {
				startPage = totalPages - maxVisible + 1; endPage = totalPages;
			} else {
				startPage = currentPage - before; endPage = currentPage + after;
			}
		}

		for (let i = startPage; i <= endPage; i++) {
			buttons.push(
				<button
					key={i}
					className={`btn btn-sm ${currentPage === i ? 'btn-primary' : 'btn-outline-primary'}`}
					onClick={() => handlePageChange(i)}
				>
					{i}
				</button>
			);
		}
		return buttons;
	};

	// ─── Render ──────────────────────────────────────────────────────────────

	return (
		<Container>
			<div className="nk-content">
				<div className="container-fluid">
					<div className="nk-content-inner">
						<div className="nk-content-body">

							{/* ── Page Header ── */}
							<div className="nk-block-head nk-block-head-sm">
								<div className="nk-block-between">
									<div className="nk-block-head-content">
										<h3 className="nk-block-title page-title">Process Returns</h3>
										<p className="text-soft">Bulk update for shipped orders — Single, Wedding &amp; Exchange orders.</p>
									</div>

									{profile?.name === 'Admin' && (
										<div className="nk-block-head-content">
											<div className="toggle-wrap nk-block-tools-toggle">
												<div className="toggle-expand-content" data-content="pageMenu">
													<ul className="nk-block-tools g-3">
														<li className="nk-block-tools-opt">
															<button
																className="btn btn-primary"
																data-bs-toggle="modal"
																data-bs-target="#processReturnsModal"
																disabled={isProcessing}
																onClick={() => {
																	// Reset to prior Saturday every time modal opens
																	setWeekEndingDate(getPriorSaturday().format('MM/DD/YYYY'));
																	setDateError('');
																}}
															>
																{isProcessing ? (
																	<>
																		<span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
																		<span>Processing…</span>
																	</>
																) : (
																	<>
																		<em className="icon ni ni-reload"></em>
																		<span>Process Returns</span>
																	</>
																)}
															</button>
														</li>
													</ul>
												</div>
											</div>
										</div>
									)}
								</div>
							</div>

							{/* ── History Card ── */}
							<div className="card card-full">
								<div className="card-inner">
									<div className="card-title-group mb-3">
										<div className="card-title">
											<h6 className="title">Processing History</h6>
										</div>
										<div className="card-tools">
											<span className="badge bg-outline-primary">
												{history.length} run{history.length !== 1 ? 's' : ''}
											</span>
										</div>
									</div>

									{/* ── History Table ── */}
									{history.length === 0 ? (
										<div className="text-center py-5 text-soft">
											<em className="icon ni ni-inbox" style={{ fontSize: 40 }}></em>
											<p className="mt-2">No processing history yet. Run your first return processing to see results here.</p>
										</div>
									) : (
										<>
											<div className="table-responsive">
												<table className="table table-striped">
													<thead>
														<tr>
															<th scope="col">Processed On</th>
															<th scope="col">Week-Ending Date</th>
															<th scope="col" className="text-center">Total Processed</th>
															<th scope="col" className="text-center">Returned</th>
															<th scope="col" className="text-center">Not Returned</th>
														</tr>
													</thead>
													<tbody>
														{paginatedHistory.map((entry, idx) => (
															<tr key={idx}>
																<td className="tableMinWidth150">
																	{entry.processedAt.format('MMMM D, YYYY')}
																	<span className="text-soft d-block" style={{ fontSize: '12px' }}>
																		at {entry.processedAt.format('h:mm a')}
																	</span>
																</td>
																<td className="tableMinWidth120">
																	{entry.weekEndingDate.format('MMMM D, YYYY')}
																</td>
																<td className="text-center">
																	<span className="badge badge-dim bg-outline-secondary">
																		{entry.total.toLocaleString()}
																	</span>
																</td>
																<td className="text-center">
																	<span className="badge badge-dim bg-outline-success">
																		{entry.returned.toLocaleString()}
																	</span>
																</td>
																<td className="text-center">
																	<span className="badge badge-dim bg-outline-danger">
																		{entry.notReturned.toLocaleString()}
																	</span>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>

											{/* ── Pagination ── */}
											{history.length > itemsPerPage && (
												<div className="nk-block-between-md g-3 mt-3">
													<div className="g">
														<div className="pagination d-flex gap-2 align-items-center">
															<button
																className={`btn btn-sm ${currentPage === 1 ? 'btn-disabled' : 'btn-primary'}`}
																onClick={() => handlePageChange(currentPage - 1)}
																disabled={currentPage === 1}
															>
																Previous
															</button>
															{renderPaginationButtons()}
															<button
																className={`btn btn-sm ${currentPage === totalPages ? 'btn-disabled' : 'btn-primary'}`}
																onClick={() => handlePageChange(currentPage + 1)}
																disabled={currentPage === totalPages}
															>
																Next
															</button>
														</div>
													</div>
													<div className="g">
														<div className="pagination-goto d-flex align-items-center gap-3">
															<div>Showing</div>
															<div>
																<select
																	className="form-select form-select-sm"
																	value={itemsPerPage}
																	onChange={handleItemsPerPageChange}
																>
																	{[10, 20, 50, 100].map(size => (
																		<option key={size} value={size}>{size}</option>
																	))}
																</select>
															</div>
															<div>of {history.length} items</div>
														</div>
													</div>
												</div>
											)}
										</>
									)}
								</div>
							</div>

						</div>
					</div>
				</div>
			</div>

			{/* ══════════════════════════════════════════
			    Confirmation Modal — Process Returns
			══════════════════════════════════════════ */}
			<div className="modal fade" tabIndex="-1" id="processReturnsModal">
				<div className="modal-dialog" role="document">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title">Process Returns</h5>
							<a
								href="#"
								className="close"
								data-bs-dismiss="modal"
								aria-label="Close"
								ref={confirmModalCloseRef}
								onClick={() => {
									setWeekEndingDate(getPriorSaturday().format('MM/DD/YYYY'));
									setDateError('');
								}}
							>
								<em className="icon ni ni-cross-c"></em>
							</a>
						</div>

						<div className="modal-body inputCardBox">
							<div className="row">
								<div className="col-md-12 mb-3">
									<p className="text-soft mb-3">
										Do you want to process returns up to the week‑ending date below?
									</p>

									<label className="form-label">Week-ending date</label>
									<div className="form-control-wrap">
										<div className="form-icon form-icon-right">
											<em className="icon ni ni-calendar"></em>
										</div>
										<input
											type="text"
											className={`form-control ${dateError ? 'is-invalid' : ''}`}
											placeholder="MM/DD/YYYY"
											value={weekEndingDate}
											onChange={(e) => {
												setWeekEndingDate(e.target.value);
												setDateError('');
											}}
										/>
										{dateError && (
											<div className="invalid-feedback">{dateError}</div>
										)}
									</div>
									<small className="text-soft mt-1 d-block">
										Applies to: Single, Wedding &amp; Exchange orders within the last 10 months.
									</small>
								</div>
							</div>

							{/* Info box */}
							<div className="alert alert-info alert-icon" style={{ borderRadius: '6px' }}>
								<em className="icon ni ni-info-fill"></em>
								<div>
									<strong>Processing rules:</strong>
									<ul className="mb-0 mt-1" style={{ paddingLeft: '18px', fontSize: '13px' }}>
										<li>Orders <strong>not</strong> on the Delinquent list: <span className="text-success">Shipped → Returned</span></li>
										<li>Orders <strong>on</strong> the Delinquent list: <span className="text-danger">Shipped → Not Returned</span></li>
									</ul>
								</div>
							</div>
						</div>

						<div className="modal-footer">
							<button
								type="button"
								className="btn btn-outline-secondary"
								onClick={handleNo}
							>
								No
							</button>
							<button
								type="button"
								className="btn btn-primary"
								onClick={handleYes}
								disabled={isProcessing}
							>
								{isProcessing ? (
									<>
										<span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
										Processing…
									</>
								) : (
									'Yes'
								)}
							</button>
						</div>
					</div>
				</div>
			</div>

		</Container>
	);
}

export default ProcessReturns;